import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Check, ScanLine, MessageCircle, CloudRain, TrendingDown, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";

const BENEFITS = [
  { icon: ScanLine, title: "Unlimited AI Car Scan", text: "Diagnose problems from a photo, any time." },
  { icon: MessageCircle, title: "Unlimited Ask VecPadi", text: "Your car expert, always on." },
  { icon: CloudRain, title: "Climate & flood alerts", text: "Protect your car from Lagos flooding." },
  { icon: ShieldCheck, title: "Priority roadside help", text: "Faster assistance when you're stuck." },
];

export default function Premium() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [plan, setPlan] = useState("yearly");
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    setLoading(true);
    try {
      const callback_url = `${window.location.origin}/app`;
      const { data } = await api.post("/subscription/initialize", { plan, email: user?.email, callback_url });
      if (data.mode === "paystack") {
        window.location.href = data.authorization_url;
        return;
      }
      // demo mode — auto verify
      await api.get(`/subscription/verify/${data.reference}`);
      await refresh();
      toast.success("Welcome to Premium! No wahala. 🎉");
      navigate("/app");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (user?.plan === "premium") {
    return (
      <div className="space-y-6" data-testid="premium-page">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-zinc-500"><ArrowLeft size={16} /> Back</button>
        <div className="rounded-3xl bg-zinc-950 p-8 text-center text-white">
          <Crown className="mx-auto text-emerald-400" size={40} />
          <h1 className="mt-4 font-display text-2xl font-bold">You're Premium! 🎉</h1>
          <p className="mt-2 text-zinc-400">Enjoy unlimited scans, AI assistant and advanced intelligence.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="premium-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-zinc-500"><ArrowLeft size={16} /> Back</button>

      <div className="rounded-3xl bg-gradient-to-br from-zinc-950 to-zinc-800 p-8 text-white text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-400"><Crown size={14} /> VecPadi Premium</div>
        <h1 className="mt-4 font-display text-3xl font-bold leading-tight">Save money. Stay safe. Zero stress.</h1>
        <p className="mx-auto mt-3 max-w-md text-zinc-300">Premium members catch problems early and avoid costly repairs. One saved breakdown pays for a whole year.</p>
      </div>

      {/* Plan toggle */}
      <div className="grid grid-cols-2 gap-3">
        <button data-testid="plan-monthly" onClick={() => setPlan("monthly")}
          className={`rounded-3xl border-2 p-5 text-left transition-colors ${plan === "monthly" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
          <div className="text-sm font-semibold text-zinc-500">Monthly</div>
          <div className="mt-1 font-display text-2xl font-bold">₦1,500</div>
          <div className="text-xs text-zinc-400">per month</div>
        </button>
        <button data-testid="plan-yearly" onClick={() => setPlan("yearly")}
          className={`relative rounded-3xl border-2 p-5 text-left transition-colors ${plan === "yearly" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
          <div className="absolute -top-2.5 right-3 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white">SAVE 17%</div>
          <div className="text-sm font-semibold text-zinc-500">Yearly</div>
          <div className="mt-1 font-display text-2xl font-bold">₦15,000</div>
          <div className="text-xs text-zinc-400">₦1,250/month</div>
        </button>
      </div>

      <div className="space-y-3">
        {BENEFITS.map((b) => (
          <div key={b.title} className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"><b.icon size={20} /></div>
            <div><div className="text-sm font-semibold text-zinc-800">{b.title}</div><div className="text-xs text-zinc-400">{b.text}</div></div>
          </div>
        ))}
      </div>

      <Button data-testid="subscribe-btn" onClick={subscribe} disabled={loading} className="w-full h-13 rounded-full bg-emerald-500 hover:bg-emerald-600 text-base gap-2">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <><Crown size={18} /> Get Premium — {plan === "yearly" ? "₦15,000/yr" : "₦1,500/mo"}</>}
      </Button>
      <p className="text-center text-xs text-zinc-400">Secured by Paystack. Cancel anytime. (Demo mode active — no real charge.)</p>
    </div>
  );
}
